import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { useSocket } from '../../context/SocketContext';
import * as messageService from '../../services/messageService';
import { canSendInquiry } from '../../utils/membershipUtils';
import SellerDashboardLayout from '../../components/SellerDashboardLayout';
import UpgradePromptModal from '../../components/UpgradePromptModal/UpgradePromptModal';
import './MessagePage.css';

const MessagePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { planFeatures, planName, planDisplayName, loading: subscriptionLoading } = useSubscription();
  const { on, off, joinConversation, leaveConversation, isConnected } = useSocket();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedConversationDetails, setSelectedConversationDetails] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const messagesEndRef = useRef(null);
  const previousConversationRef = useRef(null);
  const menuRef = useRef(null);

  const currentUserId = user?.id;

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle menu toggle
  const handleMenuToggle = (e, convId) => {
    e.stopPropagation();
    if (openMenuId === convId) {
      setOpenMenuId(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.right - 160
      });
      setOpenMenuId(convId);
    }
  };

  // Handle mark as read
  const handleMarkAsRead = async (e, conv) => {
    e.stopPropagation();
    setOpenMenuId(null);
    try {
      await messageService.markAsRead(conv.id, currentUserId);
      setConversations(prev => prev.map(c => 
        c.id === conv.id ? { ...c, isRead: true } : c
      ));
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  // Handle delete conversation
  const handleDeleteConversation = async (e, conv) => {
    e.stopPropagation();
    setOpenMenuId(null);
    if (window.confirm('Are you sure you want to delete this conversation?')) {
      try {
        await messageService.deleteConversation(conv.id, currentUserId);
        setConversations(prev => prev.filter(c => c.id !== conv.id));
        if (selectedConversation?.id === conv.id) {
          setSelectedConversation(null);
          setSelectedConversationDetails(null);
        }
      } catch (err) {
        console.error('Error deleting conversation:', err);
        alert('Failed to delete conversation');
      }
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [selectedConversationDetails?.messages]);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!currentUserId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await messageService.getConversations(currentUserId);
      setConversations(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [currentUserId]);

  // Fetch messages for a conversation
  const fetchMessages = useCallback(async (conversationId) => {
    if (!currentUserId) return;
    try {
      const data = await messageService.getMessages(conversationId, currentUserId);
      setSelectedConversationDetails(data);
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  }, [currentUserId]);

  useEffect(() => {
    if (currentUserId) {
      fetchConversations();
    }
  }, [fetchConversations, currentUserId]);

  // WebSocket: Handle real-time message updates
  useEffect(() => {
    if (!currentUserId || !isConnected) return;

    // Handle new messages in current conversation
    const handleNewMessage = ({ conversationId, message }) => {
      if (selectedConversation?.id === conversationId) {
        setSelectedConversationDetails(prev => {
          if (!prev) return prev;
          // Avoid duplicate messages
          const messageExists = prev.messages?.some(m => m.id === message.id);
          if (messageExists) return prev;
          return {
            ...prev,
            messages: [...(prev.messages || []), message]
          };
        });
      }
    };

    // Handle conversation list updates (new messages in other conversations)
    const handleConversationUpdated = ({ conversationId, preview, time, isRead }) => {
      setConversations(prev => prev.map(conv => {
        if (conv.id === conversationId) {
          return { ...conv, preview, time, isRead };
        }
        return conv;
      }));
    };

    // Handle new conversations
    const handleNewConversation = () => {
      // Refetch conversations when a new one is created
      fetchConversations();
    };

    // Handle typing indicator
    const handleUserTyping = ({ userId, conversationId, isTyping: typing }) => {
      if (selectedConversation?.id === conversationId && userId !== currentUserId) {
        setIsTyping(typing);
      }
    };

    // Subscribe to socket events
    on('new_message', handleNewMessage);
    on('conversation_updated', handleConversationUpdated);
    on('new_conversation', handleNewConversation);
    on('user_typing', handleUserTyping);

    // Cleanup
    return () => {
      off('new_message', handleNewMessage);
      off('conversation_updated', handleConversationUpdated);
      off('new_conversation', handleNewConversation);
      off('user_typing', handleUserTyping);
    };
  }, [currentUserId, isConnected, selectedConversation?.id, on, off, fetchConversations]);

  // WebSocket: Join/leave conversation rooms
  useEffect(() => {
    if (!isConnected) return;

    // Leave previous conversation room
    if (previousConversationRef.current) {
      leaveConversation(previousConversationRef.current);
    }

    // Join new conversation room
    if (selectedConversation?.id) {
      joinConversation(selectedConversation.id);
      previousConversationRef.current = selectedConversation.id;
    }

    return () => {
      if (selectedConversation?.id) {
        leaveConversation(selectedConversation.id);
      }
    };
  }, [selectedConversation?.id, isConnected, joinConversation, leaveConversation]);

  // Handle conversation query parameter (from RFQ reply)
  useEffect(() => {
    const handleConversationParam = async () => {
      if (!currentUserId || conversations.length === 0) return;
      
      const params = new URLSearchParams(location.search);
      const conversationId = params.get('conversation');
      
      if (conversationId) {
        const convId = parseInt(conversationId);
        const conv = conversations.find(c => c.id === convId);
        
        if (conv) {
          handleSelectConversation(conv);
          // Clear the URL parameter
          window.history.replaceState({}, document.title, location.pathname);
        } else {
          // Conversation not in list, try to fetch messages directly
          try {
            await fetchMessages(convId);
            setSelectedConversation({ id: convId });
            window.history.replaceState({}, document.title, location.pathname);
          } catch (err) {
            console.error('Could not load conversation:', err);
          }
        }
      }
    };
    handleConversationParam();
  }, [location.search, conversations, currentUserId]);

  // Handle conversation selection
  const handleSelectConversation = async (conv) => {
    setSelectedConversation(conv);
    await fetchMessages(conv.id);
    setConversations(prev => prev.map(c => 
      c.id === conv.id ? { ...c, isRead: true } : c
    ));
  };

  // Handle inquiry from product page
  useEffect(() => {
    const handleInquiry = async () => {
      if (!currentUserId) return;
      
      // Check if user has Basic plan - they cannot send inquiries
      // Admin bypass - admin can do everything
      const isAdminUser = user?.adminRole === 'admin' || user?.adminRole === 'subadmin';
      if (!isAdminUser && !canSendInquiry(planName)) {
        setShowUpgradeModal(true);
        return;
      }
      
      if (location.state?.fromInquiry && location.state?.items) {
        const inquiryItems = location.state.items;
        const inquiryMessage = location.state.message || '';
        try {
          await messageService.sendInquiry(currentUserId, inquiryItems, inquiryMessage);
          await fetchConversations();
          window.history.replaceState({}, document.title);
        } catch (err) {
          console.error('Error sending inquiry:', err);
          if (err.code === 'MESSAGING_NOT_ALLOWED') {
            alert('Basic plan users cannot send inquiries. Please upgrade your plan.');
          }
        }
      }
    };
    handleInquiry();
  }, [location.state, currentUserId, fetchConversations, planName]);

  // Send message
  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedConversation || sendingMessage) return;
    
    // Check if user has Basic plan - they cannot send messages
    // Admin bypass - admin can do everything
    if (!(user?.adminRole === 'admin' || user?.adminRole === 'subadmin') && !canSendInquiry(planName)) {
      setShowUpgradeModal(true);
      return;
    }
    
    try {
      setSendingMessage(true);
      const newMessage = await messageService.sendMessage(
        selectedConversation.id,
        currentUserId,
        messageText
      );
      // Add message only if it doesn't already exist (avoid duplicates from socket)
      setSelectedConversationDetails(prev => {
        if (!prev) return prev;
        const messageExists = prev.messages?.some(m => m.id === newMessage.id);
        if (messageExists) return prev;
        return {
          ...prev,
          messages: [...(prev?.messages || []), newMessage]
        };
      });
      setConversations(prev => prev.map(conv => {
        if (conv.id === selectedConversation.id) {
          return { ...conv, preview: messageText, time: 'Just now' };
        }
        return conv;
      }));
      setMessageText('');
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSendingMessage(false);
    }
  };

  // Filter conversations based on search
  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const senderName = conv.sender?.name?.toLowerCase() || '';
    const preview = conv.preview?.toLowerCase() || '';
    const companyName = conv.sender?.company?.toLowerCase() || '';
    return senderName.includes(query) || preview.includes(query) || companyName.includes(query);
  });

  // Login prompt for non-authenticated users
  if (!user) {
    return (
      <SellerDashboardLayout>
        <div className="msg-login-prompt">
          <div className="msg-login-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
              <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2>Please Login</h2>
          <p>You need to be logged in to view your messages.</p>
          <Link to="/" className="msg-login-link">Go to Home to Login</Link>
        </div>
      </SellerDashboardLayout>
    );
  }

  // Check if user can reply to messages (only GCC Business and Global Enterprise)
  // Basic plan users can VIEW messages but CANNOT reply
  // Admin bypass - admin can do everything
  const isAdminUser = user?.adminRole === 'admin' || user?.adminRole === 'subadmin';
  const canReplyToMessages = isAdminUser || planFeatures?.canLiveChat === true;

  return (
    <SellerDashboardLayout>
      <div className="msg-page-content">
        <div className="msg-container">
          {/* Left Panel - Inbox List */}
          <div className="msg-inbox-panel">
            <div className="msg-inbox-header">
              <h2>Inbox</h2>
              <div className="msg-inbox-actions">
                <button 
                  className={`msg-action-btn ${showSearch ? 'active' : ''}`}
                  onClick={() => setShowSearch(!showSearch)}
                  title="Search"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Search Bar */}
            {showSearch && (
              <div className="msg-search-bar">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="#9E9E9E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  type="text"
                  placeholder="Search messages..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
                {searchQuery && (
                  <button className="msg-clear-search" onClick={() => setSearchQuery('')}>×</button>
                )}
              </div>
            )}

            {/* Inbox List */}
            <div className="msg-inbox-list">
              {loading ? (
                <div className="msg-inbox-loading">
                  <div className="msg-spinner"></div>
                  <span>Loading messages...</span>
                </div>
              ) : error ? (
                <div className="msg-inbox-error">
                  <span>{error}</span>
                  <button onClick={fetchConversations}>Retry</button>
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="msg-inbox-empty">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                    <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="#DADADA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {searchQuery.trim() ? (
                    <>
                      <span>No results found</span>
                      <p>No messages match "{searchQuery}"</p>
                      <button className="msg-clear-search-btn" onClick={() => setSearchQuery('')}>Clear search</button>
                    </>
                  ) : (
                    <>
                      <span>No messages yet</span>
                      <p>Start an inquiry to message sellers</p>
                    </>
                  )}
                </div>
              ) : (
                filteredConversations.map((conv, index) => (
                  <React.Fragment key={conv.id}>
                    <div 
                      className={`msg-inbox-item ${selectedConversation?.id === conv.id ? 'active' : ''} ${!conv.isRead ? 'unread' : 'read'}`}
                      onClick={() => handleSelectConversation(conv)}
                    >
                      <div className="msg-inbox-avatar">
                        <img src={conv.sender.avatar} alt={conv.sender.name} />
                        {!conv.isRead && <span className="msg-unread-dot"></span>}
                      </div>
                      <div className="msg-inbox-content">
                        <div className="msg-inbox-row">
                          <div className="msg-inbox-user">
                            <div className="msg-inbox-name-line">
                              <span className="msg-inbox-name">{conv.sender.name} </span>
                              <span className="msg-inbox-role">{conv.sender.role} {conv.type && '| '}</span>
                              {conv.type && <span className="msg-inbox-type">{conv.type}</span>}
                            </div>
                          </div>
                          <div className="msg-inbox-meta">
                            <span className="msg-inbox-time">{conv.time}</span>
                            <div className="msg-inbox-menu-wrapper" ref={openMenuId === conv.id ? menuRef : null}>
                              <button className="msg-inbox-menu" onClick={(e) => handleMenuToggle(e, conv.id)}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                  <circle cx="12" cy="5" r="2" fill="#6D6D6D"/>
                                  <circle cx="12" cy="12" r="2" fill="#6D6D6D"/>
                                  <circle cx="12" cy="19" r="2" fill="#6D6D6D"/>
                                </svg>
                              </button>
                              {openMenuId === conv.id && (
                                <div 
                                  className="msg-inbox-dropdown"
                                  style={{ top: menuPosition.top, left: menuPosition.left }}
                                >
                                  <button onClick={(e) => handleMarkAsRead(e, conv)}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                      <polyline points="22 4 12 14.01 9 11.01"/>
                                    </svg>
                                    Mark as Read
                                  </button>
                                  <button onClick={(e) => handleDeleteConversation(e, conv)}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <polyline points="3 6 5 6 21 6"/>
                                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                    </svg>
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <p className="msg-inbox-preview">{conv.preview}</p>
                      </div>
                    </div>
                    {index < filteredConversations.length - 1 && <div className="msg-inbox-divider"></div>}
                  </React.Fragment>
                ))
              )}
            </div>
          </div>

          {/* Right Panel - Chat View */}
          <div className={`msg-chat-panel ${selectedConversation ? 'has-conversation' : ''}`}>
            {selectedConversation && selectedConversationDetails ? (
              <>
                {/* Chat Header */}
                <div className="msg-chat-header">
                  <div className="msg-chat-user">
                    <img 
                      src={selectedConversationDetails.sender?.avatar || selectedConversation.sender.avatar} 
                      alt={selectedConversationDetails.sender?.name || selectedConversation.sender.name}
                      className="msg-chat-avatar"
                    />
                    <div className="msg-chat-user-info">
                      <span className="msg-chat-name">
                        {selectedConversationDetails.sender?.name || selectedConversation.sender.name}
                      </span>
                      <span className="msg-chat-role">
                        {selectedConversationDetails.sender?.role || selectedConversation.sender.role}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Chat Messages */}
                <div className="msg-chat-messages">
                  {(selectedConversationDetails.messages || []).map((msg, index) => (
                    <div key={msg.id || index} className={`msg-message ${msg.isOwn ? 'own' : 'other'}`}>
                      {!msg.isOwn && (
                        <img src={msg.senderAvatar} alt="Sender" className="msg-message-avatar" />
                      )}
                      <div className="msg-message-content">
                        {/* RFQ Message - styled like Product Inquiry */}
                        {msg.type === 'rfq' && msg.products ? (
                          <div className="msg-inquiry-card">
                            <div className="msg-inquiry-badge">Product Inquiry</div>
                            <div className="msg-inquiry-products">
                              {msg.products.map((product, idx) => (
                                <div key={idx} className="msg-inquiry-product">
                                  <img 
                                    src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=100&h=100&fit=crop" 
                                    alt={product.category} 
                                    className="msg-product-img"
                                  />
                                  <div className="msg-product-info">
                                    <p className="msg-product-name">{product.details || product.category}</p>
                                    <div className="msg-product-price">
                                      <span className="msg-price">
                                        {product.offerPrice ? `$${product.offerPrice} - $${product.offerPrice}` : 'Price on request'}
                                      </span>
                                      {product.unit && <span className="msg-unit">/ {product.unit}</span>}
                                    </div>
                                    {product.quantity && (
                                      <span className="msg-product-moq">{product.quantity} (MOQ)</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="msg-inquiry-footer">
                              Inquiry for {msg.products.length} product(s)
                            </div>
                          </div>
                        ) : /* Inquiry Message with Products */
                        (msg.type === 'inquiry' && msg.products) ? (
                          <div className="msg-inquiry-card">
                            <div className="msg-inquiry-badge">Product Inquiry</div>
                            <div className="msg-inquiry-products">
                              {msg.products.map((product, idx) => (
                                <div key={idx} className="msg-inquiry-product">
                                  <img 
                                    src={product.image} 
                                    alt={product.description || product.name} 
                                    className="msg-product-img"
                                  />
                                  <div className="msg-product-info">
                                    <p className="msg-product-name">{product.description || product.name}</p>
                                    <div className="msg-product-price">
                                      <span className="msg-price">{product.price}</span>
                                      {product.unit && <span className="msg-unit">/ {product.unit}</span>}
                                    </div>
                                    {product.moq && (
                                      <span className="msg-product-moq">{product.moq} (MOQ)</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="msg-inquiry-footer">
                              Inquiry for {msg.products.length} product(s)
                            </div>
                          </div>
                        ) : (
                          <div className="msg-text-bubble">
                            <p>{msg.content}</p>
                          </div>
                        )}
                        <span className="msg-message-time">{msg.time}</span>
                      </div>
                      {msg.isOwn && (
                        <img src={msg.senderAvatar} alt="You" className="msg-message-avatar" />
                      )}
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input - Only show for users with canLiveChat permission */}
                {canReplyToMessages ? (
                  <div className="msg-input-container">
                    <input
                      type="text"
                      placeholder="Type message"
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                      disabled={sendingMessage}
                    />
                    <div className="msg-input-actions">
                      <button className="msg-attach-btn" title="Attach file">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M16.5 6V17.5C16.5 19.71 14.71 21.5 12.5 21.5C10.29 21.5 8.5 19.71 8.5 17.5V5C8.5 3.62 9.62 2.5 11 2.5C12.38 2.5 13.5 3.62 13.5 5V15.5C13.5 16.05 13.05 16.5 12.5 16.5C11.95 16.5 11.5 16.05 11.5 15.5V6H10V15.5C10 16.88 11.12 18 12.5 18C13.88 18 15 16.88 15 15.5V5C15 2.79 13.21 1 11 1C8.79 1 7 2.79 7 5V17.5C7 20.54 9.46 23 12.5 23C15.54 23 18 20.54 18 17.5V6H16.5Z" fill="#1F396C"/>
                        </svg>
                      </button>
                      <button 
                        className={`msg-send-btn ${sendingMessage ? 'sending' : ''}`}
                        onClick={handleSendMessage}
                        disabled={sendingMessage || !messageText.trim()}
                      >
                        {sendingMessage ? (
                          <div className="msg-send-spinner"></div>
                        ) : (
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <rect width="24" height="24" fill="#1F396C"/>
                            <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="white"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="msg-upgrade-banner">
                    <div className="msg-upgrade-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#F59E0B" strokeWidth="2"/>
                        <path d="M12 8V12" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M12 16H12.01" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <div className="msg-upgrade-text">
                      <span>Messaging is only available with <strong>GCC Business</strong> or <strong>Global Enterprise</strong> plan. Upgrade to send and receive messages.</span>
                    </div>
                    <button 
                      className="msg-upgrade-btn"
                      onClick={() => navigate('/membership-packages')}
                    >
                      Upgrade Now
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="msg-no-chat">
                <div className="msg-no-chat-icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                    <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="#DADADA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3>Select a conversation</h3>
                <p>Choose a message from the inbox to start chatting</p>
              </div>
            )}
          </div>
        </div>

        {/* Upgrade Modal */}
        <UpgradePromptModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          feature="Send Messages"
          message="Basic plan users cannot send messages. Please upgrade to GCC Business or Global Enterprise plan to access messaging."
        />
      </div>
    </SellerDashboardLayout>
  );
};

export default MessagePage;
